import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Database, FileText, Users, TrendingUp, Cpu, Code, Brain } from 'lucide-react';
import logoImage from '../assets/icons/logo.png';
import EnhancedChatInput from '../components/EnhancedChatInput';
import { MessageRequest } from '../schemas/chat';
import { useKnowledgeBase } from '../contexts/KnowledgeBaseContext';
import { useModel } from '../contexts/ModelContext';
import { useLiterature } from '../contexts/LiteratureContext';
import { useAuth } from '../contexts/AuthContext';
import { researchAPI } from '../services/api/researchApi';
import { RecommendedQuestion } from '../schemas/chat';
import { fetchRecommendedQuestions } from '../utils/recommendedQuestionsCache';
import FeatureCard from '../components/home/FeatureCard';
import ExampleQuestionCard from '../components/home/ExampleQuestionCard';
import ExampleQuestionSkeleton from '../components/home/ExampleQuestionSkeleton';

// Background decoration component
const BackgroundDecoration: React.FC = () => (
  <div className='fixed inset-0 pointer-events-none z-0 opacity-50'>
    {/* Responsive background blur circles - different sizes for mobile vs desktop */}
    <div
      className='absolute rounded-full bg-[#FDFFE8]'
      style={{
        width: 'min(280px, 40vw)',
        height: 'min(280px, 40vw)',
        filter: 'blur(min(300px, 20vw))',
        top: '-13px',
        left: '-141px',
      }}
    />
    <div
      className='absolute rounded-full bg-[#D0D0FF]'
      style={{
        width: 'min(244px, 35vw)',
        height: 'min(244px, 35vw)',
        filter: 'blur(min(300px, 18vw))',
        top: '204px',
        left: '89px',
      }}
    />
  </div>
);

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    state: kbState,
    toggleKnowledgeBase,
    selectAllKnowledgeBases,
    clearSelectedKnowledgeBases,
  } = useKnowledgeBase();
  const { models } = useModel();
  const { state: literatureState, setSelectedSources, setOptions } = useLiterature();
  const { user } = useAuth();
  const [selectedModel, setSelectedModel] = useState('DeepSeek-V3.1');
  const [recommendedQuestions, setRecommendedQuestions] = useState<RecommendedQuestion[]>([]);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(true);
  const hasFetchedRef = useRef(false);

  // Fetch recommended questions on component mount (only once)
  useEffect(() => {
    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }

    const loadQuestions = async () => {
      try {
        hasFetchedRef.current = true;
        setIsQuestionsLoading(true);

        const questions = await fetchRecommendedQuestions(async () => {
          const response = await researchAPI.getHomeRecommendedQuestions();
          return response.data;
        }, user?.id?.toString() ?? null);

        setRecommendedQuestions(questions);
      } catch (error) {
        console.error('Failed to fetch recommended questions:', error);
        // Fallback to hardcoded suggestions if API fails
        setRecommendedQuestions([]);
      } finally {
        setIsQuestionsLoading(false);
      }
    };

    loadQuestions();

    // Cleanup function to reset the fetch flag when component unmounts
    return () => {
      // Don't reset hasFetchedRef here - we want it to persist for the session
    };
  }, [user?.id]);

  const handleSuggestionClick = (suggestion: string) => {
    // Navigate directly with the suggestion as the message request
    navigate('/chat', { state: { messageRequest: { content: suggestion } } });
  };

  const handleEnhancedSendMessage = (data: MessageRequest) => {
    if (data.content.trim()) {
      // Pass the entire messageRequest including file metadata
      navigate('/chat', {
        state: {
          messageRequest: data,
          // Also pass files separately for easier access in ChatPage
          files: data.files,
        },
      });
    }
  };

  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
  };

  // Feature cards configuration
  const featureCards = [
    {
      icon: <Database className='text-blue-500' />,
      title: t('pages.homePage.featureKnowledgeQA', '资料库问答'),
      description: t('pages.homePage.featureKnowledgeQADesc', '基于您上传的文档进行精准问答'),
      onClick: () => navigate('/knowledge'),
    },
    {
      icon: <FileText className='text-purple-500' />,
      title: t('pages.homePage.featurePaperWriting', '论文写作'),
      description: t('pages.homePage.featurePaperWritingDesc', '智能辅助论文撰写与润色'),
    },
    {
      icon: <Users className='text-green-500' />,
      title: t('pages.homePage.featureAcademicExchange', '学术交流'),
      description: t('pages.homePage.featureAcademicExchangeDesc', '连接研究者，分享研究成果'),
    },
    {
      icon: <TrendingUp className='text-orange-500' />,
      title: t('pages.homePage.featureTrendPrediction', '趋势预测'),
      description: t('pages.homePage.featureTrendPredictionDesc', '分析领域前沿与发展趋势'),
    },
  ];

  // Example questions with icons
  const exampleQuestionIcons = [
    <Cpu size={18} className='text-blue-500' />,
    <Code size={18} className='text-purple-500' />,
    <Brain size={18} className='text-green-500' />,
  ];

  // Get example questions (either from API or fallback)
  const getExampleQuestions = (): string[] => {
    if (recommendedQuestions.length >= 3) {
      return recommendedQuestions.slice(0, 3).map(q => q.question);
    }
    // Fallback to translation keys
    return [
      t('pages.homePage.suggestion1'),
      t('pages.homePage.suggestion2'),
      t('pages.homePage.suggestion3'),
    ];
  };

  const exampleQuestions = getExampleQuestions();

  return (
    <div
      className='relative min-h-screen flex flex-col items-center px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 overflow-x-hidden'
      style={{ backgroundColor: '#FAFAFA' }}
    >
      {/* Background decoration from Figma */}
      <BackgroundDecoration />

      <div className='relative z-10 max-w-6xl w-full flex flex-col'>
        {/* Header with logo */}
        <div className='flex flex-col items-center mb-8 sm:mb-10 lg:mb-12'>
          <img
            src={logoImage}
            alt='NeverEnd AI'
            className='h-16 sm:h-20 lg:h-24 xl:h-28 transition-all duration-300 mb-4 sm:mb-6'
          />
          <h1 className='text-2xl sm:text-3xl lg:text-4xl font-bold text-[#202224] mb-2 sm:mb-3 text-center'>
            {t('pages.homePage.title', '开始您的科研探索')}
          </h1>
          <p className='text-sm sm:text-base lg:text-lg text-gray-500 text-center max-w-2xl'>
            {t('pages.homePage.subtitle', '结合知识库与学术文献，为您提供专业的科研问答服务')}
          </p>
        </div>

        {/* Feature Cards Section - Reduced spacing */}
        <div className='mb-6 sm:mb-8 px-2 sm:px-0'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6'>
            {featureCards.map((card, index) => (
              <FeatureCard
                key={index}
                icon={card.icon}
                title={card.title}
                description={card.description}
                onClick={card.onClick}
              />
            ))}
          </div>
        </div>

        {/* Enhanced Chat Input Area - Hero positioning */}
        <div className='flex justify-center px-2 sm:px-0 mb-10 sm:mb-12'>
          <EnhancedChatInput
            onSendMessage={handleEnhancedSendMessage}
            placeholder={t('pages.homePage.inputPlaceholder')}
            selectedModel={selectedModel}
            models={models}
            onSelectModel={handleModelChange}
            kbState={kbState}
            onToggleKnowledgeBase={toggleKnowledgeBase}
            onSelectAllKnowledgeBases={selectAllKnowledgeBases}
            onClearAllKnowledgeBases={clearSelectedKnowledgeBases}
            literatureSources={literatureState.selectedSources}
            onSelectLiterature={setSelectedSources}
            literatureOptions={literatureState.options}
            onSelectLiteratureOptions={setOptions}
          />
        </div>

        {/* Example Questions Section - Subtle footer styling */}
        <div className='px-2 sm:px-0'>
          <div className='flex flex-col items-center'>
            <p className='text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 font-medium tracking-wide'>
              {t('pages.homePage.questionSuggestions')}
            </p>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 w-full max-w-4xl'>
              {isQuestionsLoading ? (
                <ExampleQuestionSkeleton count={3} />
              ) : (
                exampleQuestions.map((question, index) => (
                  <ExampleQuestionCard
                    key={index}
                    icon={exampleQuestionIcons[index % exampleQuestionIcons.length]}
                    question={question}
                    onClick={() => handleSuggestionClick(question)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
